import React from 'react';
import moment from 'moment-timezone';

import Strings from '../../../../Strings';
import { actions } from '../redux/actions';
import { actions as scheduleActions } from '../../Schedules/redux/actions';
import {
  dateFarInTheFuture,
  engagementMessages,
  getWindowOffsetsWithPrefix,
  remindersTypes,
} from '../../../../modals/EditRpmSchedule/EditRpmScheduleModal';
import { DATE_MONTH_DAY_YEAR, TIMEZONES, TIME_FORMAT_12_LOWERCASE } from '../../../../constants';
import { getMedsType, toolTipForTable } from '../../../../utils';
import { getProgramDisplayName } from '../../../../utils/cmsPrograms';
import { ORG_FEATURES, orgFeatureEnabled } from '../../../../utils/userPermissions';


export const getLocalDate = (dt, timezone) => {
  if (!dt) {
    return '-';
  }
  const m = moment(dt);
  if (m.isSameOrAfter(moment(dateFarInTheFuture).subtract(1, 'day'))) {
    return Strings.startsAlongWithEnrollment;
  }
  return m.tz(timezone).format(DATE_MONTH_DAY_YEAR);
};

export const getTimesValue = (medication, withTimezone) => {
  if (medication.format?.type === 'as_needed') {
    return {
      display: '-',
      tooltip: '-',
    };
  }

  let timeLabel = '-';
  if (Array.isArray(medication.format?.offset) && medication.format.offset.length > 0) {
    timeLabel = medication.format.offset
      .map(time =>
        moment
          .unix(time)
          .utc()
          .format(TIME_FORMAT_12_LOWERCASE),
      )
      .join(',');
  } else if (Array.isArray(medication.format?.times) && medication.format.times.length > 0) {
    timeLabel = medication.format.times.join(',');
  }

  const scheduleTimezone = medication.scheduleTimezone || medication.timezone;
  if (withTimezone && scheduleTimezone) {
    const timezoneEntry = TIMEZONES.find(tz => tz.timezone === scheduleTimezone);
    if (timezoneEntry) {
      const text = `${timeLabel} (${timezoneEntry.text})`;
      return {
        display: text,
        tooltip: text,
      };
    }
    const warning = `Unsupported timezone: ${scheduleTimezone}. Please update it.`;
    return {
      display: (
        <span>
          {timeLabel} (<span className="unsupported-timezone">{warning}</span>)
        </span>
      ),
      tooltip: `${timeLabel} (${warning})`,
    };
  }

  return {
    display: timeLabel,
    tooltip: timeLabel,
  };
};

// Helper to check if a reminder is enabled (supports both old and new API format)
const isReminderEnabled = reminder => {
  if (!reminder) return false;
  if (typeof reminder.enabled === 'boolean') return reminder.enabled;
  return true;
};

export const getReminderTexts = medication => {
  const smsReminders = medication.sms_reminders || medication.sms_reminder;
  if (!smsReminders) return ['-'];
  const enabled = Object.entries(smsReminders)
    .filter(([, value]) => isReminderEnabled(value))
    .map(
      ([key, value]) =>
        `${Strings.patient_medications.remindersTypes[key]}${key === remindersTypes.missedDoses
          ? ` (${value.offset})`
          : value?.offset
            ? ` (${key === remindersTypes.early ? '-' : '+'}${value.offset / 60} ${Strings.unitsEnum.minutes})`
            : ''
        }`,
    );
  return enabled.length > 0 ? enabled : ['-'];
};

export const getMilestoneTexts = medication => {
  const milestones = medication.milestone_reminders || medication.milestone_config;
  if (!milestones) return ['-'];
  const enabled = Object.entries(milestones)
    .filter(([, value]) => isReminderEnabled(value))
    .map(
      ([key, value]) =>
        `${Strings.patient_medications.reminders[key]}${key === engagementMessages.streak_achievement
          ? ` (${value.thresholds?.join(', ')})`
          : ''
        }`,
    );
  return enabled.length > 0 ? enabled : ['-'];
};

export const getTakeWindowText = medication =>
  medication.takeWindow && medication.takeWindow.length === 2 && (medication.takeWindow[0] || medication.takeWindow[1])
    ? `${getWindowOffsetsWithPrefix('-').find(w => w.value === medication.takeWindow[0])?.label}, ${getWindowOffsetsWithPrefix('+').find(w => w.value === medication.takeWindow[1])?.label
    }`
    : Strings.allDay;

export const getScheduleProgramName = (schedule, patientEnrollments) => {
  const enrollmentData = patientEnrollments?.find(e => e.enrollmentId === schedule.enrollmentId);
  return enrollmentData ? getProgramDisplayName(enrollmentData.name, enrollmentData.subprogram) : '-';
};

// The schedule's [label, value] rows, in the card's order. capCell renders the assigned cap (or the attach action);
// withTimezone appends the schedule timezone to the times.
export const getScheduleDetailsRows = ({ schedule, capCell, patientEnrollments, withTimezone = true }) => {
  const createdOn = getLocalDate(schedule.createdAt, schedule.scheduleTimezone);
  const editedOn = getLocalDate(schedule.editedAt, schedule.scheduleTimezone);
  const createdEditedLabel = schedule.editedAt
    ? `${Strings.patient_medications.createdOn} / ${Strings.patient_medications.editedOn}`
    : Strings.patient_medications.createdOn;
  const createdEditedValue = schedule.editedAt ? `${createdOn} / ${editedOn}` : createdOn;

  const data = [
    [Strings.patient_medications.assignedCap, capCell],
    [Strings.patient_medications.schedule, getMedsType(schedule.format.type)],
    [
      Strings.patient_medications.times,
      (() => {
        const { display, tooltip } = getTimesValue(schedule, withTimezone);
        return <span className="times-tooltip" title={tooltip}>{display}</span>;
      })(),
    ],
    [Strings.patient_medications.startDate, getLocalDate(schedule.startDate, schedule.scheduleTimezone)],
    [Strings.patient_medications.endDate, getLocalDate(schedule.endDate, schedule.scheduleTimezone)],
    [createdEditedLabel, createdEditedValue],
  ];
  // SIG and notes rows only when set, so schedules without them look as before.
  if (schedule.format?.sig) {
    data.push([Strings.patient_medications.sig, schedule.format.sig]);
  }
  if (schedule.format?.notes) {
    data.push([Strings.patient_medications.medicationNotes, toolTipForTable(schedule.format.notes, [schedule.format.notes])]);
  }
  const remindersText = getReminderTexts(schedule);
  const milestonesText = getMilestoneTexts(schedule);
  const dataExt = [
    [Strings.adherence, schedule.analytics?.adherence ? `${schedule.analytics?.adherence}%` : '-'],
    [Strings.reminders, toolTipForTable(remindersText.join(', '), remindersText)],
    [Strings.administrationWindow, getTakeWindowText(schedule)],
    [Strings.program, getScheduleProgramName(schedule, patientEnrollments)],
  ];
  if (orgFeatureEnabled(ORG_FEATURES.engagement_messages_enabled)) {
    dataExt.push([Strings.capPatient.engagementMessages, toolTipForTable(milestonesText.join(', '), milestonesText)]);
  }
  return [...data, ...dataExt];
};

// confirmation-modal data for the schedule actions; each refreshes the patient's schedules once confirmed.
export const deleteScheduleConfirmation = (patientId, schedule) => ({
  title: (
    <span>
      {Strings.deleteScheduleWarning} <b>{schedule.medicationName}</b>?
    </span>
  ),

  onConfirmAction: scheduleActions.deleteSchedule(patientId, schedule.rootId),
  confirmPostAction: actions.getMedications(patientId),
  onCancelAction: null,
});

export const assignCapConfirmation = (patientId, schedule, capId) => ({
  caption: Strings.confirmAssignment,
  title: <span>{Strings.formatString(Strings.assignCapToScheduleCaption, capId, schedule.medicationName)}</span>,
  hideCaution: true,
  onConfirmAction: scheduleActions.editSchedule(patientId, schedule.rootId, { cap_id: capId }),
  onCancelAction: null,
  confirmPostAction: actions.getMedications(patientId),
});

export const unassignCapConfirmation = (patientId, schedule) => ({
  caption: Strings.confirmDelete,
  title: (
    <span>
      {Strings.formatString(Strings.unassignCapToScheduleCaption, schedule?.capId, schedule?.medicationName || schedule.id)}
    </span>
  ),
  hideCaution: true,
  onConfirmAction: scheduleActions.editSchedule(patientId, schedule.rootId, { cap_id: '' }),
  onCancelAction: null,
  confirmPostAction: actions.getMedications(patientId),
});
